// src/utils/listeProblemes.ts — SCE Issues
// Règles de la fenêtre « liste de problèmes » (30/09, maquette validée) :
// filtres par pastille, tri, regroupement par machine, recherche. Pur, testable.
//
// Deux sortes de listes :
//  - `age`    : « À qualifier » — tous sont Ouverts et à qualifier, le statut et
//               la criticité ne disent rien ; ce qui sépare les lignes, c'est
//               l'ancienneté. Pastilles par métier ; défaut : plus ancien d'abord.
//  - `statut` : listes du dashboard, problèmes suivis — statut et criticité
//               varient d'une ligne à l'autre. Pastilles par statut ; défaut :
//               criticité d'abord.

import {
  CRITICITES_ORDRE,
  STATUTS_ORDRE,
  cleRapprochement,
  estActif,
  type Issue,
  type Metier,
  type Statut,
} from '@/types/issue.types'
import { METIERS_ORDRE } from '@/config/metiers.config'

export type ColonneListe = 'age' | 'statut'
export type TriListe = 'ancien' | 'recent' | 'criticite' | 'machine'

export const LIBELLE_TRI: Record<TriListe, string> = {
  ancien: 'Du plus ancien au plus récent',
  recent: 'Du plus récent au plus ancien',
  criticite: 'Par criticité',
  machine: 'Par équipement',
}

/** Libellé court du bouton de tri (bureau). */
export const LIBELLE_TRI_COURT: Record<TriListe, string> = {
  ancien: 'Plus ancien d’abord',
  recent: 'Plus récent d’abord',
  criticite: 'Criticité',
  machine: 'Équipement',
}

/** Sous le titre : ce que dit l'ordre en cours. */
export const SOUS_TITRE_TRI: Record<TriListe, string> = {
  ancien: 'Du plus ancien au plus récent',
  recent: 'Du plus récent au plus ancien',
  criticite: 'Du plus critique au moins critique',
  machine: 'Regroupés par équipement',
}

/** La criticité ne trie rien dans une liste où tout est « À qualifier ». */
export function trisProposes(colonne: ColonneListe): readonly TriListe[] {
  return colonne === 'age' ? ['ancien', 'recent', 'machine'] : ['criticite', 'ancien', 'recent', 'machine']
}

export function triParDefaut(colonne: ColonneListe): TriListe {
  return colonne === 'age' ? 'ancien' : 'criticite'
}

// ─── Pastilles de filtre ───

export interface OptionFiltre {
  cle: string
  libelle: string
  n: number
  /** Pour les pastilles de métier : la couleur se lit sur le métier. */
  metier?: Metier
}

/**
 * Les pastilles à proposer : les métiers présents (liste « age »), les statuts
 * présents (liste « statut »). Moins de deux valeurs : rien à filtrer, null.
 */
export function optionsFiltre(
  issues: readonly Issue[],
  colonne: ColonneListe,
  libelleMetier: (m: Metier) => string,
): OptionFiltre[] | null {
  const options: OptionFiltre[] =
    colonne === 'age'
      ? METIERS_ORDRE.map((m) => ({ cle: m, libelle: libelleMetier(m), n: issues.filter((i) => i.metier === m).length, metier: m }))
      : STATUTS_ORDRE.map((s) => ({ cle: s, libelle: s, n: issues.filter((i) => i.statut === s).length }))
  const presentes = options.filter((o) => o.n > 0)
  return presentes.length >= 2 ? presentes : null
}

/** Filtre par pastille (null = tous) puis par texte (titre, N°, machine, déclarant). */
export function filtrer(
  issues: readonly Issue[],
  colonne: ColonneListe,
  cle: string | null,
  texte: string,
): Issue[] {
  const t = cleRapprochement(texte)
  return issues.filter((i) => {
    if (cle !== null && (colonne === 'age' ? i.metier !== cle : i.statut !== (cle as Statut))) return false
    if (!t) return true
    return [i.id, i.titre, i.numeroSerie, i.declarantNom].some((v) => cleRapprochement(v).includes(t))
  })
}

// ─── Tri et regroupement ───

export type EntreeListe =
  | { type: 'groupe'; cle: string; machine: string; n: number }
  | { type: 'ligne'; issue: Issue }

function date(i: Issue): number {
  return Date.parse(i.dateDeclaration) || 0
}

const parAnciennete = (a: Issue, b: Issue): number => date(a) - date(b)

function rangCriticite(i: Issue): number {
  const r = CRITICITES_ORDRE.indexOf(i.criticite)
  return r === -1 ? CRITICITES_ORDRE.length : r
}

/**
 * Les lignes dans l'ordre choisi. « Par machine » insère un titre par machine :
 * la machine dont le plus vieux problème attend depuis le plus longtemps
 * d'abord, chaque machine avec ses problèmes du plus ancien au plus récent.
 * « Par criticité » : les terminés à la fin, ils ne demandent plus rien.
 */
export function organiser(issues: readonly Issue[], tri: TriListe): EntreeListe[] {
  if (tri === 'machine') {
    const groupes = new Map<string, Issue[]>()
    for (const i of issues) {
      const cle = cleRapprochement(i.numeroSerie) || '—'
      const liste = groupes.get(cle)
      if (liste) liste.push(i)
      else groupes.set(cle, [i])
    }
    return [...groupes.entries()]
      .map(([cle, liste]) => ({ cle, liste: liste.slice().sort(parAnciennete) }))
      .sort((a, b) => date(a.liste[0]!) - date(b.liste[0]!))
      .flatMap(({ cle, liste }): EntreeListe[] => [
        { type: 'groupe', cle, machine: liste[0]!.numeroSerie || 'Sans équipement', n: liste.length },
        ...liste.map((issue): EntreeListe => ({ type: 'ligne', issue })),
      ])
  }
  const tries = issues.slice().sort((a, b) => {
    if (tri === 'recent') return date(b) - date(a)
    if (tri === 'criticite') {
      return Number(estActif(b)) - Number(estActif(a)) || rangCriticite(a) - rangCriticite(b) || parAnciennete(a, b)
    }
    return parAnciennete(a, b)
  })
  return tries.map((issue): EntreeListe => ({ type: 'ligne', issue }))
}

export function libelleNombre(n: number): string {
  return `${n} problème${n > 1 ? 's' : ''}`
}
